/**
 * CR-8252 (R5, option A, Aaron 27 Oct 2026): Supplier Responses lists both
 * kinds of questionnaire — onboarding questionnaires (the one system,
 * decision 4) and the older ad hoc ones — as one list. Each row keeps where
 * its own review screen is.
 */
import type { FilledDataset, SupplierQualification } from '../../lib/api';

/** A Supplier Responses row: an ad hoc questionnaire, or an onboarding one shaped like it. */
export type ResponseRow = SupplierQualification & {
  /** Which system the row comes from. */
  kind: 'onboarding' | 'ad_hoc';
  /** Where its review screen is. */
  review_href: string;
};

/** Onboarding statuses mapped onto the list's status keys. */
const ONBOARDING_STATUS: Record<FilledDataset['status'], SupplierQualification['status']> = {
  not_started: 'invited',
  in_progress: 'in_progress',
  submitted: 'submitted',
  under_review: 'under_review',
  more_info_required: 'more_info_required',
  approved: 'approved',
  rejected: 'rejected',
  expired: 'expired',
};

/**
 * An ad hoc questionnaire as a row.
 *
 * @param q - the qualification
 * @returns the row, opening the qualification review
 */
export function rowFromQualification(q: SupplierQualification): ResponseRow {
  return { ...q, kind: 'ad_hoc', review_href: `/intelligence/qualifications/${q.id}` };
}

/**
 * An onboarding questionnaire as a row. Its column in the matrix view is keyed
 * by the negative questionnaire id, so it can never collide with an ad hoc
 * template id.
 *
 * @param fd - the onboarding questionnaire sent to a supplier
 * @returns the row, opening the onboarding review
 */
export function rowFromFilledDataset(fd: FilledDataset): ResponseRow {
  return {
    id: fd.id,
    template: -fd.dataset,
    template_name: fd.dataset_name || 'Questionnaire',
    template_type: fd.dataset_type,
    supplier: fd.supplier,
    supplier_name: fd.supplier_name,
    customer: fd.customer,
    customer_name: fd.customer_name,
    status: ONBOARDING_STATUS[fd.status] ?? 'invited',
    score_percentage: fd.score_percentage ?? null,
    passed: fd.passed,
    invited_at: fd.assigned_at,
    submitted_at: fd.submitted_at,
    reviewed_at: fd.reviewed_at,
    expires_at: fd.expires_at ?? null,
    // Whole percent, as the ad hoc rows show it (the API sends a decimal string).
    completion_percentage: Math.round(Number(fd.completion_percentage) || 0),
    created_at: fd.assigned_at ?? '',
    kind: 'onboarding',
    review_href: `/onboarding/review/${fd.id}`,
  } as ResponseRow;
}

/**
 * The list status filter, as the onboarding API's status.
 *
 * @param filterStatus - the page's filter ('all' or a list status)
 * @returns the onboarding status to filter on, or undefined for all
 */
export function onboardingStatusFor(filterStatus: string): string | undefined {
  if (filterStatus === 'all') return undefined;
  return filterStatus === 'invited' ? 'not_started' : filterStatus;
}
